import { useMemo } from "react";
import { useQuery } from "@apollo/client/react";
import getDataAvailability from "../graphql/queries/getDataAvailability";
import { ProductClass } from "../types/enums";
import type { ValidatedYearRange } from "../types/branded";
import { tryValidatedYearRange } from "../types/branded";

/**
 * API-shaped year range as returned by the GraphQL `DataAvailability` type.
 *
 * For domain logic (validation, range arithmetic), convert to `ValidatedYearRange`
 * using `toValidatedYearRange()`.
 */
export interface YearRange {
  yearMin: number;
  yearMax: number;
}

/**
 * Convert an API `YearRange` to a domain `ValidatedYearRange`.
 * Returns `null` if the range is invalid (yearMin > yearMax).
 */
export function toValidatedYearRange(
  range: YearRange | null | undefined,
): ValidatedYearRange | null {
  if (!range) return null;
  return tryValidatedYearRange(range.yearMin, range.yearMax);
}

/** Convert a string value to a ProductClass enum member, defaulting to HS92. */
export function toProductClass(value: string): ProductClass {
  const values = new Set(Object.values(ProductClass) as string[]);
  if (values.has(value)) {
    return value as ProductClass;
  }
  console.warn(`[toProductClass] Unknown product class value: "${value}", defaulting to HS92`);
  return ProductClass.HS92Products;
}

interface DataAvailabilityItem {
  productClassification?: string | null;
  yearMin: number;
  yearMax: number;
}

/**
 * Hook to fetch and access data availability metadata from the API
 * Returns year ranges for each product classification
 */
export const useDataAvailability = () => {
  const { loading, error, data } = useQuery<{
    dataAvailability: (DataAvailabilityItem | null)[];
  }>(getDataAvailability, {
    fetchPolicy: "cache-first",
  });

  const availabilityRows = data?.dataAvailability || [];

  // Memoize all helper functions so consumers get stable references.
  // This prevents unnecessary re-renders when these are used as deps or props.
  const helpers = useMemo(() => {
    const getYearRangeForProductClass = (
      productClass: ProductClass,
    ): YearRange | null => {
      if (!availabilityRows.length) return null;

      const availability = availabilityRows.find(
        (item) =>
          item?.productClassification != null &&
          String(item.productClassification) === String(productClass),
      );

      if (!availability) {
        if (availabilityRows.length > 0) {
          console.warn(
            `[useDataAvailability] Product classification "${productClass}" not found in API response. ` +
              `Available classifications: ${availabilityRows.map((d) => d?.productClassification).join(", ")}. ` +
              `Using fallback year range.`,
          );
          const rows = availabilityRows.filter(
            (x): x is NonNullable<typeof x> => x != null,
          );
          if (rows.length === 0) return null;
          const latestRange = rows.reduce((latest, current) =>
            (current.yearMax ?? 0) > (latest.yearMax ?? 0) ? current : latest,
          );
          return {
            yearMin: latestRange.yearMin ?? 0,
            yearMax: latestRange.yearMax ?? 0,
          };
        }
        return null;
      }

      return {
        yearMin: availability.yearMin,
        yearMax: availability.yearMax,
      };
    };

    const getAvailableClassifications = (): ProductClass[] => {
      if (!availabilityRows.length) return [];
      return availabilityRows.filter(Boolean).map((item) =>
        toProductClass(String(item!.productClassification)),
      );
    };

    const getLatestYear = (): number | null => {
      if (!availabilityRows.length) return null;
      const years = availabilityRows
        .map((item) => item?.yearMax)
        .filter((y): y is number => y != null && !Number.isNaN(y));
      return years.length ? Math.max(...years) : null;
    };

    const getEarliestYear = (): number | null => {
      if (!availabilityRows.length) return null;
      const years = availabilityRows
        .map((item) => item?.yearMin)
        .filter((y): y is number => y != null && !Number.isNaN(y));
      return years.length ? Math.min(...years) : null;
    };

    return {
      getYearRangeForProductClass,
      getAvailableClassifications,
      getLatestYear,
      getEarliestYear,
    };
  }, [availabilityRows]);

  return {
    loading,
    error,
    dataAvailability: availabilityRows,
    ...helpers,
  };
};
